// Market tab auto-run — Story #6837 Stream 8a-1c.
//
// One model call per request (API Gateway cuts every request at 29 s), so the
// client drives the chain itself: classify (when the invention was never
// categorised), then ALWAYS pestle → swot → range. A pestle step whose row is
// under 90 days old costs nothing and consumes no quota, and sending swot on
// its own would 409 step_order when the hook-classified project has no PESTLE
// row yet — so the chain is never shortened.
//
// runMarketChain owns every chain rule and takes its three API calls as plain
// `deps`, so the test hands it a hand-rolled fake (no module mock).
// nextAutoRunAction is the pure mount-time decision.
import { useCallback, useEffect, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { getMarket, postMarketAnalyseStep, postMarketClassify } from '../../services/api'
import type {
  MarketAnalyseRequest, MarketAnalyseResult, MarketAnalyseStep, MarketClassifyResult, MarketProject,
} from '../../types/market'
import { invalidateMarket } from './marketQueries'

// ---- Poll timing (copied from whitespace/decomposeStatus.ts) --------------
/** 202 / 504 / network timeout mean "still running", not "failed": poll the GET. */
export const MARKET_POLL_MS = 3_000
export const MARKET_POLL_MAX_MS = 5 * 60_000

const CHAIN: readonly MarketAnalyseStep[] = ['pestle', 'swot', 'range']

export type MarketRunPhase =
  | 'idle'
  | 'classifying'
  | MarketAnalyseStep
  | 'running'
  | 'rate_limited'
  | 'insufficient_basis'
  | 'error'
  | 'done'

export interface MarketRunResult {
  phase: MarketRunPhase
  error: string | null
  /** Seconds, read from the 429 BODY (the gateway strips Retry-After). */
  retryAfter: number | null
}

export type AutoRunAction = 'classify-then-chain' | 'chain' | 'none' | 'gate'

export interface MarketChainDeps {
  postMarketClassify: (projectId: string, opts?: { force?: boolean }) => Promise<MarketClassifyResult>
  postMarketAnalyseStep: (projectId: string, body: MarketAnalyseRequest) => Promise<MarketAnalyseResult>
  getMarket: (projectId: string, country?: string) => Promise<MarketProject>
}

export interface MarketChainOptions {
  country: string
  /** Only an explicit button click sends true; a mount auto-run never does. */
  force: boolean
  classifyFirst: boolean
  /** Progress callback for the status banner. */
  onPhase?: (phase: MarketRunPhase, retryAfter?: number | null) => void
  pollMs?: number
  pollMaxMs?: number
  sleep?: (ms: number) => Promise<void>
}

// Resolved at call time, not import time: screens whose tests stub services/api
// with an explicit factory import this module without ever running a chain.
const realApi: MarketChainDeps = {
  postMarketClassify: (projectId, opts) => postMarketClassify(projectId, opts),
  postMarketAnalyseStep: (projectId, body) => postMarketAnalyseStep(projectId, body),
  getMarket: (projectId, country) => getMarket(projectId, country),
}

// ---- Pure rules -----------------------------------------------------------

/** True when a computed section is missing or out of date. */
function needsRun(item: { stale: boolean } | null | undefined): boolean {
  return !item || item.stale
}

/** Rules (a)–(c): what a freshly mounted Market tab should do on its own. */
export function nextAutoRunAction(
  market: MarketProject | undefined,
  canRun: boolean,
  running: boolean,
): AutoRunAction {
  if (!market || running) return 'none'
  if (market.status === 'insufficient_basis') return 'gate'
  if (!canRun) return 'none'
  if (market.status === null) return 'classify-then-chain'
  if (market.status === 'completed' && (needsRun(market.swot) || needsRun(market.range))) return 'chain'
  return 'none'
}

/** Where to resume after a 504 / poll: the first missing or stale item. */
function firstMissingStep(market: MarketProject): number {
  if (!market.pestle) return 0
  if (needsRun(market.swot)) return 1
  if (needsRun(market.range)) return 2
  return -1
}

// ---- Error classification -------------------------------------------------

type HttpErr = {
  response?: { status?: number; data?: { error?: unknown; retry_after?: unknown } | string }
  code?: string
  message?: string
}

function isStillRunning(err: unknown): boolean {
  const e = err as HttpErr | null | undefined
  if (!e) return false
  if (e.response?.status === 504) return true
  if (e.code === 'ECONNABORTED' || e.code === 'ETIMEDOUT') return true
  return /status code 504|timeout|network error/i.test(e.message ?? '')
}

function bodyOf(err: unknown): { error?: unknown; retry_after?: unknown } {
  const d = (err as HttpErr | null | undefined)?.response?.data
  return d && typeof d === 'object' ? d : {}
}

function errorText(err: unknown): string {
  const body = bodyOf(err)
  if (typeof body.error === 'string' && body.error) return body.error
  const e = err as HttpErr | null | undefined
  return e?.message || 'The market analysis could not be completed.'
}

type Outcome<T> =
  | { kind: 'ok'; data: T }
  | { kind: 'running' }
  | { kind: 'rate_limited'; retryAfter: number | null }
  | { kind: 'step_order' }
  | { kind: 'fail'; message: string }

async function attempt<T extends { status?: string }>(call: () => Promise<T>): Promise<Outcome<T>> {
  try {
    const data = await call()
    // 202 {status:'running'} resolves like a success.
    if (data && (data as { status?: string }).status === 'running') return { kind: 'running' }
    return { kind: 'ok', data }
  } catch (err) {
    const status = (err as HttpErr | null | undefined)?.response?.status
    if (status === 429) {
      const ra = bodyOf(err).retry_after
      return { kind: 'rate_limited', retryAfter: typeof ra === 'number' ? ra : null }
    }
    if (status === 409 && bodyOf(err).error === 'step_order') return { kind: 'step_order' }
    if (isStillRunning(err)) return { kind: 'running' }
    return { kind: 'fail', message: errorText(err) }
  }
}

// ---- The chain ------------------------------------------------------------

const realSleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms))

/**
 * One chain per project at a time. The server's run lease already stops a second paid call, but without this a
 * remount (tab away and back inside the cache window) or a Regenerate click would start a second client chain
 * beside the first. A non-forced caller joins the chain already running; a forced re-run is refused, because the
 * running chain would not honour its `force`.
 */
const inFlight = new Map<string, Promise<MarketRunResult>>()

export function runMarketChain(
  projectId: string,
  opts: MarketChainOptions,
  deps: MarketChainDeps = realApi,
): Promise<MarketRunResult> {
  const running = inFlight.get(projectId)
  if (running) {
    if (opts.force) {
      return Promise.resolve({ phase: 'error', error: 'An analysis is already running for this invention. Wait for it to finish, then try again.', retryAfter: null })
    }
    return running
  }
  const chain = runChain(projectId, opts, deps).finally(() => { inFlight.delete(projectId) })
  inFlight.set(projectId, chain)
  return chain
}

async function runChain(
  projectId: string,
  opts: MarketChainOptions,
  deps: MarketChainDeps,
): Promise<MarketRunResult> {
  const { country, force } = opts
  const say = opts.onPhase ?? (() => {})
  const sleep = opts.sleep ?? realSleep
  const pollMs = opts.pollMs ?? MARKET_POLL_MS
  const pollMaxMs = opts.pollMaxMs ?? MARKET_POLL_MAX_MS
  const result = (phase: MarketRunPhase, error: string | null = null, retryAfter: number | null = null): MarketRunResult =>
    ({ phase, error, retryAfter })

  /** Poll the GET every pollMs until the run lease is released, or give up. */
  const pollUntilIdle = async (): Promise<MarketProject | null> => {
    say('running')
    for (let waited = 0; waited < pollMaxMs; waited += pollMs) {
      await sleep(pollMs)
      try {
        const m = await deps.getMarket(projectId, country)
        if (!m.running) return m
      } catch {
        // A failed poll is not a failed run: keep polling until the limit.
      }
    }
    return null
  }
  const gaveUp = 'This is taking longer than expected. Reload the tab in a minute — the analysis keeps running on the server.'

  // 429: the body's retry_after is shown. If another run holds the lease, wait
  // for it once and carry on; otherwise (cooldown / quota) stop — no loop.
  const afterRateLimit = async (retryAfter: number | null): Promise<MarketProject | MarketRunResult> => {
    say('rate_limited', retryAfter)
    let current: MarketProject
    try {
      current = await deps.getMarket(projectId, country)
    } catch {
      return result('rate_limited', null, retryAfter)
    }
    if (!current.running) return result('rate_limited', null, retryAfter)
    const settled = await pollUntilIdle()
    return settled ?? result('error', gaveUp)
  }
  const isResult = (x: MarketProject | MarketRunResult): x is MarketRunResult => 'phase' in x

  if (opts.classifyFirst) {
    let retriedClassify = false
    let rateLimitedOnce = false
    for (;;) {
      say('classifying')
      const out = await attempt<MarketClassifyResult>(() => deps.postMarketClassify(projectId, force ? { force: true } : {}))
      if (out.kind === 'fail') return result('error', out.message)
      if (out.kind === 'step_order') return result('error', 'Unexpected response while categorising.')
      if (out.kind === 'rate_limited') {
        if (rateLimitedOnce) return result('rate_limited', null, out.retryAfter)
        rateLimitedOnce = true
        const next = await afterRateLimit(out.retryAfter)
        if (isResult(next)) return next
        if (next.status === 'completed') break
        if (next.status === 'insufficient_basis') return result('insufficient_basis')
        continue
      }
      if (out.kind === 'running') {
        const settled = await pollUntilIdle()
        if (!settled) return result('error', gaveUp)
        if (settled.status === 'completed') break
        if (settled.status === 'insufficient_basis') return result('insufficient_basis')
        return result('error', 'The categorisation did not finish. Try again.')
      }
      if (out.data.status === 'completed') break
      if (out.data.status === 'insufficient_basis') return result('insufficient_basis')
      if (out.data.status === 'timeout' && !retriedClassify) {
        retriedClassify = true
        continue
      }
      return result('error', out.data.status === 'timeout' ? 'The categorisation timed out twice. Try again.' : 'The categorisation failed. Try again.')
    }
  }

  let i = 0
  let restarted = false
  let rateLimitedOnce = false
  const retriedStep = new Set<MarketAnalyseStep>()
  // A step that answers "still running" and is still missing once the lease clears (the server died before it
  // saved) is sent again ONCE; a second time is an error. Each send is a paid model call.
  const resentAfterRunning = new Set<MarketAnalyseStep>()
  while (i < CHAIN.length) {
    const step = CHAIN[i]
    say(step)
    const out = await attempt<MarketAnalyseResult>(() => deps.postMarketAnalyseStep(projectId, { step, country, force }))

    if (out.kind === 'fail') return result('error', out.message)

    if (out.kind === 'step_order') {
      // The server needs an earlier step: re-read the state and restart ONCE.
      if (restarted) return result('error', 'The analysis steps are out of order. Try again.')
      restarted = true
      try {
        const m = await deps.getMarket(projectId, country)
        i = Math.max(firstMissingStep(m), 0)
      } catch (err) {
        return result('error', errorText(err))
      }
      continue
    }

    if (out.kind === 'rate_limited') {
      if (rateLimitedOnce) return result('rate_limited', null, out.retryAfter)
      rateLimitedOnce = true
      const next = await afterRateLimit(out.retryAfter)
      if (isResult(next)) return next
      const at = firstMissingStep(next)
      if (at < 0) return result('done')
      i = at
      continue
    }

    if (out.kind === 'running') {
      const settled = await pollUntilIdle()
      if (!settled) return result('error', gaveUp)
      const at = firstMissingStep(settled)
      if (at < 0) return result('done')
      if (at === i) {
        if (resentAfterRunning.has(step)) return result('error', `The ${step} step did not finish on the server. Try again later.`)
        resentAfterRunning.add(step)
      }
      i = at
      continue
    }

    switch (out.data.status) {
      case 'done':
        i += 1
        break
      case 'insufficient_basis':
        return result('insufficient_basis')
      case 'timeout':
        if (retriedStep.has(step)) return result('error', `The ${step} step timed out twice. Try again.`)
        retriedStep.add(step)
        break
      default:
        return result('error', `The ${step} step failed. Try again.`)
    }
  }
  return result('done')
}

// ---- The hook -------------------------------------------------------------

export interface UseMarketAutoRunArgs {
  projectId: string
  /** undefined while the GET is loading — the hook then does nothing. */
  market: MarketProject | undefined
  country: string
  canRun: boolean
  /** Tests hand in a fake; the app uses the real API. */
  deps?: MarketChainDeps
  pollMs?: number
  pollMaxMs?: number
  sleep?: (ms: number) => Promise<void>
}

export interface MarketAutoRunState extends MarketRunResult {
  /** Re-run the mount rule once, after an error or a 429. */
  retry: () => void
}

/** Mount auto-runs always use AUS and never force. */
const AUTO_COUNTRY = 'AUS'

export function useMarketAutoRun(args: UseMarketAutoRunArgs): MarketAutoRunState {
  const { projectId, market, country, canRun, deps, pollMs, pollMaxMs, sleep } = args
  const qc = useQueryClient()
  const started = useRef(false)
  const mounted = useRef(true)
  const [state, setState] = useState<MarketRunResult>({ phase: 'idle', error: null, retryAfter: null })

  useEffect(() => {
    mounted.current = true
    return () => { mounted.current = false }
  }, [])

  const start = useCallback((classifyFirst: boolean) => {
    started.current = true
    void runMarketChain(
      projectId,
      {
        country: AUTO_COUNTRY,
        force: false,
        classifyFirst,
        pollMs,
        pollMaxMs,
        sleep,
        onPhase: (phase, retryAfter) => {
          if (mounted.current) setState({ phase, error: null, retryAfter: retryAfter ?? null })
        },
      },
      deps,
    ).then((res) => {
      if (mounted.current) setState(res)
      void invalidateMarket(qc, projectId)
    })
  }, [projectId, deps, pollMs, pollMaxMs, sleep, qc])

  // Another run holds the lease: show "running" and re-read the GET until it ends.
  const running = market?.running === true
  useEffect(() => {
    if (!running || started.current) return
    setState((s) => (s.phase === 'idle' ? { phase: 'running', error: null, retryAfter: null } : s))
    const timer = setInterval(() => { void qc.invalidateQueries({ queryKey: ['market', projectId] }) }, pollMs ?? MARKET_POLL_MS)
    return () => clearInterval(timer)
  }, [running, projectId, pollMs, qc])

  // The mount rule, once per mount, on the AUS view only.
  useEffect(() => {
    if (started.current || !market || country !== AUTO_COUNTRY) return
    const action = nextAutoRunAction(market, canRun, market.running)
    if (action === 'none') {
      if (!market.running) setState((s) => (s.phase === 'running' ? { phase: 'idle', error: null, retryAfter: null } : s))
      return
    }
    if (action === 'gate') {
      started.current = true
      setState({ phase: 'insufficient_basis', error: null, retryAfter: null })
      return
    }
    start(action === 'classify-then-chain')
  }, [market, country, canRun, start])

  const retry = useCallback(() => {
    if (!market) return
    const action = nextAutoRunAction(market, canRun, false)
    start(action === 'classify-then-chain')
  }, [market, canRun, start])

  return { ...state, retry }
}
